/**
 * Agency portal sidebar (SYR-85).
 *
 * Same shell language as the marketer-view sidebar (amber-soft active pill,
 * 248/76 widths) with agency destinations: Portfolio, Compare, Activity as the
 * primary intelligence surfaces, and a Manage group (Billing / Team / Settings
 * / Support) in the footer.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
    AppstoreOutlined,
    BarChartOutlined,
    HistoryOutlined,
    CreditCardOutlined,
    TeamOutlined,
    SettingOutlined,
    CustomerServiceOutlined,
} from '@ant-design/icons';
import { THEME } from '../../components/MarketerView/config/theme';

interface AgencySidebarProps {
    collapsed: boolean;
    onNavigate?: () => void;
}

const LogoIcon: React.FunctionComponent<{ size?: number }> = ({ size = 36 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="20" stroke="#3B82F6" strokeWidth="2" opacity="0.3" />
        <circle cx="24" cy="24" r="10" fill="#F59E0B" />
        <circle cx="24" cy="4" r="4" fill="#3B82F6" />
        <circle cx="6" cy="34" r="4" fill="#22C55E" />
        <circle cx="42" cy="34" r="4" fill="#3B82F6" />
    </svg>
);

const PRIMARY_NAV = [
    {
        key: 'portfolio',
        path: '/agency',
        exact: true,
        icon: <AppstoreOutlined />,
        labelId: 'agency.nav.portfolio',
        defaultLabel: 'Portfolio',
    },
    {
        key: 'compare',
        path: '/agency/compare',
        exact: false,
        icon: <BarChartOutlined />,
        labelId: 'agency.nav.compare',
        defaultLabel: 'Compare',
    },
    {
        key: 'activity',
        path: '/agency/activity',
        exact: false,
        icon: <HistoryOutlined />,
        labelId: 'agency.nav.activity',
        defaultLabel: 'Activity',
    },
];

const MANAGE_NAV = [
    {
        key: 'billing',
        path: '/agency/billing',
        icon: <CreditCardOutlined />,
        labelId: 'agency.nav.billing',
        defaultLabel: 'Billing',
    },
    {
        key: 'team',
        path: '/agency/team',
        icon: <TeamOutlined />,
        labelId: 'agency.nav.team',
        defaultLabel: 'Team',
    },
    {
        key: 'settings',
        path: '/agency/settings',
        icon: <SettingOutlined />,
        labelId: 'agency.nav.settings',
        defaultLabel: 'Settings',
    },
    {
        key: 'support',
        path: '/agency/support',
        icon: <CustomerServiceOutlined />,
        labelId: 'agency.nav.support',
        defaultLabel: 'Support',
    },
];

export const AgencySidebar: React.FunctionComponent<AgencySidebarProps> = ({
    collapsed,
    onNavigate,
}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path: string, exact: boolean): boolean =>
        exact
            ? location.pathname === path || location.pathname === `${path}/`
            : location.pathname.startsWith(path);

    const go = (path: string): void => {
        navigate(path);
        onNavigate?.();
    };

    const itemStyle = (active: boolean): React.CSSProperties => ({
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: collapsed ? '11px 0' : '11px 14px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        margin: '2px 0',
        borderRadius: 10,
        border: 'none',
        cursor: 'pointer',
        background: active ? THEME.primarySoft : 'transparent',
        color: active ? THEME.primaryDark : THEME.textBase,
        fontWeight: active ? 600 : 500,
        fontSize: 14,
        textAlign: 'left',
        transition: 'background 0.15s, color 0.15s',
        boxSizing: 'border-box',
    });

    return (
        <div
            style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
            }}
        >
            {/* Logo */}
            <div
                style={{
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    borderBottom: `1px solid ${THEME.borderSubtle}`,
                    marginBottom: 8,
                    cursor: 'pointer',
                }}
                onClick={() => go('/agency')}
            >
                <LogoIcon size={36} />
                {!collapsed && (
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.1 }}>
                            <span style={{ color: '#1f2937' }}>BrandScience</span>
                            <span style={{ color: THEME.textMuted }}>.ai</span>
                        </div>
                        <div style={{ fontSize: 12, color: THEME.textSecondary }}>
                            {intl.formatMessage({
                                id: 'agency.shell.tagline',
                                defaultMessage: 'Agency Portal',
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Primary nav */}
            <nav
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '4px 8px 0',
                }}
            >
                {PRIMARY_NAV.map((item) => {
                    const active = isActive(item.path, item.exact);
                    return (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => go(item.path)}
                            style={itemStyle(active)}
                            title={intl.formatMessage({
                                id: item.labelId,
                                defaultMessage: item.defaultLabel,
                            })}
                        >
                            <span style={{ fontSize: 18, display: 'inline-flex' }}>{item.icon}</span>
                            {!collapsed && (
                                <span>
                                    {intl.formatMessage({
                                        id: item.labelId,
                                        defaultMessage: item.defaultLabel,
                                    })}
                                </span>
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Manage group */}
            <div
                style={{
                    borderTop: `1px solid ${THEME.borderSubtle}`,
                    padding: '8px',
                    overflowX: 'hidden',
                }}
            >
                {!collapsed && (
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            color: THEME.textMuted,
                            padding: '6px 14px 4px',
                        }}
                    >
                        {intl.formatMessage({
                            id: 'agency.nav.manage',
                            defaultMessage: 'Manage',
                        })}
                    </div>
                )}
                {MANAGE_NAV.map((item) => {
                    const active = isActive(item.path, false);
                    return (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => go(item.path)}
                            style={{
                                ...itemStyle(active),
                                color: active ? THEME.primaryDark : THEME.textSecondary,
                            }}
                            title={intl.formatMessage({
                                id: item.labelId,
                                defaultMessage: item.defaultLabel,
                            })}
                        >
                            <span style={{ fontSize: 16, display: 'inline-flex' }}>{item.icon}</span>
                            {!collapsed && (
                                <span>
                                    {intl.formatMessage({
                                        id: item.labelId,
                                        defaultMessage: item.defaultLabel,
                                    })}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
